import React, { useState, useEffect } from "react";
import { MediaItem } from "./MediaItem.model";
import { UnifiedAuthenticatedImage } from "@/components/ui/UnifiedAuthenticatedImage";
import { API_CONFIG } from "@/config/api";
import { mediaCacheUtils } from "@/core/MediaLibrary/hooks/useMediaQuery";
import { UserRole } from "@/services/urlService";
import { logger } from "@/utils/logger";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";

const API_URL = API_CONFIG.URL;

// Helper to get access token
const getAccessToken = (): string | null => {
  const storedUser = localStorage.getItem("user");
  if (storedUser) {
    try {
      const user = JSON.parse(storedUser);
      return user.accessToken || null;
    } catch {
      return null;
    }
  }
  return null;
};

interface MediaItemLabelModalProps {
  isOpen: boolean;
  onClose: () => void;
  mediaItem: MediaItem;
  clientId: string;
  onLabelUpdate?: (updatedMediaItem: MediaItem) => void;
}

export const MediaItemLabelModal: React.FC<MediaItemLabelModalProps> = ({
  isOpen,
  onClose,
  mediaItem,
  clientId,
  onLabelUpdate,
}) => {
  const [label, setLabel] = useState(mediaItem.label || "");
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setLabel(mediaItem.label || "");
      setError(null);
    }
  }, [isOpen, mediaItem.label]);

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);

    try {
      const accessToken = getAccessToken();
      if (!accessToken) {
        throw new Error("Access token required to update media item");
      }

      const response = await fetch(
        `${API_URL}/admin/media/${mediaItem.id}`,
        {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            clientId: clientId,
            label: label,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update media item');
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Update operation failed');
      }

      const updatedMediaItem = MediaItem.fromBackend(
        result.mediaItem,
        UserRole.ADMIN,
        clientId
      );

      mediaCacheUtils.upsertMediaItem(clientId, updatedMediaItem);

      logger.info(`Updated label for media item ${mediaItem.id}`);
      onLabelUpdate?.(updatedMediaItem);
      onClose();
    } catch (err) {
      logger.error("Failed to update label:", err);
      setError(err instanceof Error ? err.message : "Failed to update label");
    } finally {
      setIsSaving(false);
    }
  };

  const imageName = mediaItem.getName();

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      header={
        <div className="flex items-center gap-3">
          <UnifiedAuthenticatedImage
            mediaItem={mediaItem}
            alt={imageName}
            className="w-10 h-10 object-cover rounded-sm"
            useThumbnail={true}
          />
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Edit Label</h3>
            <p className="text-xs text-gray-500 truncate max-w-[200px]">{imageName}</p>
          </div>
        </div>
      }
      footer={
        <>
          <Button
            variant="secondary"
            size="sm"
            onClick={onClose}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </>
      }
    >
      <div className="p-6 space-y-3">
        <div>
          <label htmlFor="media-label" className="block text-xs font-medium text-gray-700 mb-1.5">
            Label
          </label>
          <textarea
            id="media-label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Add a descriptive label..."
            rows={2}
            className="w-full px-3 py-2 border border-gray-200 rounded-sm text-sm focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            disabled={isSaving}
            autoFocus
          />
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-sm p-2 text-xs text-red-600">
            {error}
          </div>
        )}
      </div>
    </Modal>
  );
};
